import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { JwtHelperService } from '@auth0/angular-jwt';

import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '@environment';

import { StorageService } from '@core/services/core/storage.service';
import { Store } from '@ngrx/store';
import { AppState } from '@redux/app.reducers';
import * as actions from '@redux/actions';
import { AgentData } from '@core/models/agent-data.model';
import { ActivatedRoute } from '@angular/router';
import { LifePlanService } from './life-plan.service';
import { ClientData } from '@core/models/client-data.model';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  readonly baseUrl = environment.financialPlanner.url;
  readonly ACCESS_TOKEN_STORAGE_KEY = 'access_token';
  readonly REFRESH_TOKEN_STORAGE_KEY = 'refresh_token';

  agentAccessToken: string | null = '';

  private agentRefreshToken: string | null = '';

  constructor(
    private httpClient: HttpClient,
    private storageService: StorageService,
    private store: Store<AppState>,
    private route: ActivatedRoute,
    private lifePlanService: LifePlanService
  ) {
    Promise.all([this.populateStoredTokens(), this.populateAgentData()]).then(
      () => {
        this.populateClientData();
      }
    );
  }

  loginAgent(data: { token: string; graphToken: string }) {
    return this.httpClient.post(`${this.baseUrl}/login`, data).pipe(
      tap((response) => {
        this.storeAgentTokens(response);
        this.populateAgentData();
        this.populateClientData();
      })
    );
  }

  loginZonaAliados(zaToken: string) {
    return this.httpClient.post(`${this.baseUrl}/login`, { zaToken }).pipe(
      tap((response) => {
        this.storeAgentTokens(response);
        this.populateAgentData();
        this.populateClientData();
      })
    );
  }

  async isAgentAuthenticated() {
    let decodedToken = null;
    const helper = new JwtHelperService();
    if (this.agentAccessToken && this.hasExpiredAgentTokens()) {
      const authTokens = await this.refreshAgentTokens().toPromise();
      if (!authTokens.accessToken) {
        this.onLogout();
      }
    }
    if (this.agentAccessToken) {
      decodedToken = helper.decodeToken(this.agentAccessToken);
    }
    return this.agentAccessToken !== null && !this.hasExpiredAgentTokens()
      ? decodedToken
      : false;
  }

  refreshAgentTokens(): Observable<{
    accessToken: string;
    refreshToken: string;
  }> {
    let headers = new HttpHeaders();
    headers = headers.append(
      'Authorization',
      `Bearer ${this.agentRefreshToken}`
    );
    return this.httpClient
      .get<{ accessToken: string; refreshToken: string }>(
        `${this.baseUrl}/login/tokens`,
        { headers }
      )
      .pipe(tap((response) => this.storeAgentTokens(response)));
  }

  storeAgentTokens(data: {
    accessToken?: string;
    refreshToken?: string;
    loginData?: any;
  }) {
    this.agentAccessToken = data.accessToken || null;
    this.agentRefreshToken = data.refreshToken || null;
    this.storageService.set(
      this.ACCESS_TOKEN_STORAGE_KEY,
      data.accessToken || ''
    );
    this.storageService.set(
      this.REFRESH_TOKEN_STORAGE_KEY,
      data.refreshToken || ''
    );
  }

  onLogout() {
    this.agentAccessToken = null;
    this.agentRefreshToken = null;
    this.storageService.remove(this.ACCESS_TOKEN_STORAGE_KEY);
    this.storageService.remove(this.REFRESH_TOKEN_STORAGE_KEY);
    // This has been commented to resolve redirects when refresh token has expired
    // this.$loggedUserRole.next('CLIENT');
    // this.$loggedUserRoute.next('clients');
  }

  private hasExpiredAgentTokens() {
    const helper = new JwtHelperService();
    return (
      this.agentAccessToken && helper.isTokenExpired(this.agentAccessToken)
    );
  }

  private populateStoredTokens() {
    this.agentAccessToken = this.storageService.get(
      this.ACCESS_TOKEN_STORAGE_KEY
    );
    this.agentRefreshToken = this.storageService.get(
      this.REFRESH_TOKEN_STORAGE_KEY
    );
  }

  private populateAgentData() {
    if (this.agentAccessToken) {
      const helper = new JwtHelperService();
      const decodedToken = helper.decodeToken(this.agentAccessToken);
      this.store.dispatch(
        actions.agentDataEdit({
          agentData: {
            agentId: decodedToken.sub ?? '',
            agentName: decodedToken.agentName ?? '',
            agentEmail: decodedToken.agentEmail ?? '',
            agentPhone: decodedToken.agentPhone ?? '',
          },
        })
      );
    }
  }

  private populateClientData() {
    this.getTokenFromQueryParams().subscribe({
      next: (id) => {
        if (!id) return;
        this.store.dispatch(
          actions.clientDataEditEncryptedToken({ encryptedToken: id })
        );
        this.getClientPersonalData(id);
      },
    });
  }

  private getClientPersonalData(id: string) {
    this.lifePlanService.getClientData(id).subscribe({
      next: (response: ClientData) => {
        const { clientBirthdate } = response;
        response.clientBirthdate = new Date(clientBirthdate);
        response.clientEncryptedToken = id;
        this.store.dispatch(actions.clientDataEdit({ clientData: response }));
      },
      error: (error) => {
        const emptyClientData: ClientData = {
          clientName: '',
          clientBirthdate: new Date(),
          clientEmail: '',
          clientPhone: '',
          clientId: '',
          clientIdentityDocumentNumber: '',
          clientSurnames: '',
          clientEncryptedToken: id ?? '',
        };
        this.store.dispatch(
          actions.clientDataEdit({ clientData: emptyClientData })
        );
      },
    });
  }

  private getTokenFromQueryParams() {
    return this.route.queryParams.pipe(
      map((queryParams) => {
        const id = queryParams.id;
        return id;
      })
    );
  }
}
